package com.rosario.juridico.ui.components

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.ScrollbarStyle
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.background
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.animateScrollBy
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private fun Modifier.attachWebKeyboardScroll(
    focusRequester: FocusRequester,
    onScrollBy: (Float) -> Unit,
    onScrollToHome: () -> Unit,
    onScrollToEnd: () -> Unit
): Modifier = this
    .focusRequester(focusRequester)
    .focusable()
    .pointerInput(Unit) {
        detectTapGestures {
            focusRequester.requestFocus()
        }
    }
    .onPreviewKeyEvent { keyEvent ->
        if (keyEvent.type == KeyEventType.KeyDown) {
            when (keyEvent.key) {
                Key.DirectionDown -> {
                    onScrollBy(180f)
                    true
                }
                Key.DirectionUp -> {
                    onScrollBy(-180f)
                    true
                }
                Key.PageDown -> {
                    onScrollBy(600f)
                    true
                }
                Key.PageUp -> {
                    onScrollBy(-600f)
                    true
                }
                Key.MoveHome, Key.Home -> {
                    onScrollToHome()
                    true
                }
                Key.MoveEnd -> {
                    onScrollToEnd()
                    true
                }
                else -> false
            }
        } else false
    }

@Composable
actual fun WebScrollableContainer(
    scrollState: ScrollState,
    modifier: Modifier,
    content: @Composable () -> Unit
) {
    val focusRequester = remember { FocusRequester() }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        delay(100)
        focusRequester.requestFocus()
    }

    val primaryColor = MaterialTheme.colorScheme.primary
    val trackColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f)
    val scrollbarStyle = remember(primaryColor) {
        ScrollbarStyle(
            minimalHeight = 16.dp,
            thickness = 10.dp,
            shape = RoundedCornerShape(5.dp),
            hoverDurationMillis = 300,
            unhoverColor = primaryColor.copy(alpha = 0.7f),
            hoverColor = primaryColor.copy(alpha = 1.0f)
        )
    }

    Box(
        modifier = modifier.attachWebKeyboardScroll(
            focusRequester = focusRequester,
            onScrollBy = { delta -> scope.launch { scrollState.animateScrollBy(delta) } },
            onScrollToHome = { scope.launch { scrollState.animateScrollTo(0) } },
            onScrollToEnd = { scope.launch { scrollState.animateScrollTo(scrollState.maxValue) } }
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(end = 12.dp)
        ) {
            content()
        }
        VerticalScrollbar(
            adapter = rememberScrollbarAdapter(scrollState),
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .fillMaxHeight()
                .padding(vertical = 4.dp, horizontal = 1.dp)
                .background(
                    color = trackColor,
                    shape = RoundedCornerShape(5.dp)
                ),
            style = scrollbarStyle
        )
    }
}

@Composable
actual fun WebScrollableContainer(
    lazyListState: LazyListState,
    modifier: Modifier,
    content: @Composable () -> Unit
) {
    val focusRequester = remember { FocusRequester() }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        delay(100)
        focusRequester.requestFocus()
    }

    val primaryColor = MaterialTheme.colorScheme.primary
    val trackColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f)
    val scrollbarStyle = remember(primaryColor) {
        ScrollbarStyle(
            minimalHeight = 16.dp,
            thickness = 10.dp,
            shape = RoundedCornerShape(5.dp),
            hoverDurationMillis = 300,
            unhoverColor = primaryColor.copy(alpha = 0.7f),
            hoverColor = primaryColor.copy(alpha = 1.0f)
        )
    }

    Box(
        modifier = modifier.attachWebKeyboardScroll(
            focusRequester = focusRequester,
            onScrollBy = { delta -> scope.launch { lazyListState.animateScrollBy(delta) } },
            onScrollToHome = { scope.launch { lazyListState.animateScrollToItem(0) } },
            onScrollToEnd = {
                scope.launch {
                    val count = lazyListState.layoutInfo.totalItemsCount
                    if (count > 0) lazyListState.animateScrollToItem(count - 1)
                }
            }
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(end = 12.dp)
        ) {
            content()
        }
        VerticalScrollbar(
            adapter = rememberScrollbarAdapter(lazyListState),
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .fillMaxHeight()
                .padding(vertical = 4.dp, horizontal = 1.dp)
                .background(
                    color = trackColor,
                    shape = RoundedCornerShape(5.dp)
                ),
            style = scrollbarStyle
        )
    }
}

@Composable
actual fun WebScrollableContainer(
    lazyGridState: LazyGridState,
    modifier: Modifier,
    content: @Composable () -> Unit
) {
    val focusRequester = remember { FocusRequester() }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        delay(100)
        focusRequester.requestFocus()
    }

    val primaryColor = MaterialTheme.colorScheme.primary
    val trackColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f)
    val scrollbarStyle = remember(primaryColor) {
        ScrollbarStyle(
            minimalHeight = 16.dp,
            thickness = 10.dp,
            shape = RoundedCornerShape(5.dp),
            hoverDurationMillis = 300,
            unhoverColor = primaryColor.copy(alpha = 0.7f),
            hoverColor = primaryColor.copy(alpha = 1.0f)
        )
    }

    Box(
        modifier = modifier.attachWebKeyboardScroll(
            focusRequester = focusRequester,
            onScrollBy = { delta -> scope.launch { lazyGridState.animateScrollBy(delta) } },
            onScrollToHome = { scope.launch { lazyGridState.animateScrollToItem(0) } },
            onScrollToEnd = {
                scope.launch {
                    val count = lazyGridState.layoutInfo.totalItemsCount
                    if (count > 0) lazyGridState.animateScrollToItem(count - 1)
                }
            }
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(end = 12.dp)
        ) {
            content()
        }
        VerticalScrollbar(
            adapter = rememberScrollbarAdapter(lazyGridState),
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .fillMaxHeight()
                .padding(vertical = 4.dp, horizontal = 1.dp)
                .background(
                    color = trackColor,
                    shape = RoundedCornerShape(5.dp)
                ),
            style = scrollbarStyle
        )
    }
}
